import { ToastService } from './../../shared/components/toast/toast.service';
import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { User } from 'src/app/models/User';
import { TwoFactorSetup, TwoFactorStatus } from 'src/app/models/Token';
import { PostbackToken, PostbackTokenState } from 'src/app/models/PostbackToken';

import { ConfirmationService } from './../../shared/components/confirmation-modal/confirmation.service';
import { SwitcherComponent } from './../../shared/switcher/switcher.component';
import { AuthService } from './../../shared/services/auth.service';
import { PostbackTokenService } from './../../shared/services/postback-token.service';
import { AGENCY_CLIENT, UserService } from './../../shared/services/user.service';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit, OnDestroy {

  user: User;
  userForm: UntypedFormGroup;
  subscriptions: Subscription[] = [];

  diffNF: boolean;
  errorCEP: boolean = false;

  oldCNPJCPF: string = '';

  operationType: string;
  
  operationTypes: any[] = [];

  // --- Verificação em duas etapas (CU-86ak84mj6) -----------------------------
  //
  // `twoFactorStep` controla o miniassistente do card:
  //   idle    → só o switch e o estado atual
  //   setup   → pediu senha, recebeu QR, falta o primeiro código
  //   codes   → ativado; mostrando os códigos de recuperação (uma única vez)
  /**
   * O switcher gira o próprio estado no clique, mas ativar exige senha e código:
   * enquanto o fluxo não termina, o visual não pode afirmar que está ativo. Esta
   * referência existe para devolvê-lo ao estado real quando o fluxo é cancelado
   * ou falha.
   */
  @ViewChild(SwitcherComponent) twoFactorSwitcher: SwitcherComponent;

  twoFactor: TwoFactorStatus = null;
  twoFactorStep: 'idle' | 'setup' | 'codes' | 'recovery' = 'idle';
  twoFactorSetup: TwoFactorSetup = null;
  twoFactorPassword = '';
  twoFactorCode = '';
  twoFactorLoadError = false;
  recoveryCodesCopied = false;
  twoFactorError: string = null;
  twoFactorRecoveryCodes: string[] = null;
  twoFactorBusy = false;

  // --- Token dos postbacks (CU-86ak8p7w0) ------------------------------------
  postbackToken: PostbackToken = null;
  postbackTokenState: PostbackTokenState = 'loading';
  postbackTokenBusy = false;

  /** Carga do cadastro e gravação — o formulário e o botão Salvar. */
  loading = false;
  isSaving = false;

  /**
   * Multiusuários (CU-17tm466h1cm): cadastro, faturamento e postback são da
   * conta, e só o dono altera (`owner_only` no backend). O membro vê os dados
   * em modo leitura; 2FA e senha continuam dele.
   */
  readonly isMember: boolean;

  get isAgency(): boolean {
    return this.user.profile_id === AGENCY_CLIENT;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private userService: UserService,
    private toast: ToastService,
    private confirmation: ConfirmationService,
    private postbackTokenService: PostbackTokenService
  ) {
    this.diffNF = false;
    this.user = authService.getUser();
    this.userService.getOperationTypes().subscribe(operation_types => this.operationTypes = operation_types);

    this.userForm = fb.group({
      id: [this.user.id],
      name: [this.user.name, [Validators.required, Validators.minLength(3)]],
      email: [this.user.email],
      phone: [this.user.phone, [Validators.required]],
      document: [this.user.document, [Validators.required]],
      pix_key: [this.user.pix_key],
      utm_source: [this.user.utm_source],
      password: [''],
      operation_type: [this.user.operation_type],
      postcode: [
        this.user.postcode,
        [Validators.required, Validators.minLength(9), Validators.maxLength(9)],
      ],
      street: [
        this.user.street,
        [Validators.required, Validators.minLength(3)],
      ],
      number: [this.user.number, Validators.required],
      complement: [this.user.complement],
      district: [
        this.user.district,
        [Validators.required, Validators.minLength(3)],
      ],
      city: [this.user.city, Validators.required],
      region: [this.user.region, Validators.required],
      billing_document: [this.user.billing_document],
      billing_company_name: [this.user.billing_company_name],
      billing_company_alias: [this.user.billing_company_alias],
      billing_email: [this.user.billing_email],
      billing_im: [this.user.billing_im],
      billing_postcode: [this.user.billing_postcode],
      billing_street: [this.user.billing_street],
      billing_number: [this.user.billing_number],
      billing_complement: [this.user.billing_complement],
      billing_district: [this.user.billing_district],
      billing_city: [this.user.billing_city],
      billing_state: [this.user.billing_state],
    });

    if (this.user.billing_document) {
      this.diffNF = true;
    }

    this.isMember = !!authService.currentMember;

    if (this.isMember) {
      this.userForm.disable();
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.userService.findById(this.user.id).subscribe({
      next: user => {
        this.loading = false;
        this.user = user;
        this.userForm.patchValue({ operation_type: this.user.operation_type })

        // O usuário da sessão do membro é a cópia que veio no login: o
        // formulário dele mostra o cadastro atual da conta. Só para o membro,
        // que não edita; o dono pode já estar digitando quando a resposta chega.
        if (this.isMember) {
          this.userForm.patchValue({ ...user, password: '' });
          this.diffNF = !!user.billing_document;
        }
      },
      error: () => { this.loading = false; },
    });

    this.loadTwoFactorStatus();
    this.loadPostbackToken();
  }

  // --- Verificação em duas etapas -------------------------------------------

  /**
   * O card inteiro é `*ngIf="twoFactor"`: sem tratamento de erro, qualquer 500
   * fazia o bloco de segundo fator SUMIR sem uma palavra, e o usuário concluía
   * que a plataforma não tem verificação em duas etapas.
   */
  loadTwoFactorStatus(): void {
    this.twoFactorLoadError = false;

    this.subscriptions.push(
      this.authService.twoFactorStatus().subscribe({
        next: status => {
          this.twoFactor = status;
          this.syncSwitcher();
        },
        error: () => {
          this.twoFactorLoadError = true;
        }
      })
    );
  }

  /**
   * Copia os códigos para a área de transferência.
   *
   * Eles aparecem uma única vez; sem isto a pessoa transcreve à mão ou tira
   * print da tela, que é pior. O `catch` cobre navegador sem permissão de
   * clipboard — nesse caso os códigos continuam visíveis na tela.
   */
  copyRecoveryCodes(): void {
    const texto = (this.twoFactorRecoveryCodes ?? []).join('\n');

    // `navigator.clipboard?.writeText(...)` devolve `undefined` quando a API não
    // existe — página em HTTP, iframe sem permissão — e o `.then` seguinte
    // estoura TypeError. O `.catch` abaixo não cobre isso: ele trata a Promise,
    // não o acesso à propriedade.
    if (!navigator.clipboard?.writeText) {
      this.toast.error('Não foi possível copiar. Anote os códigos da tela.');
      return;
    }

    navigator.clipboard.writeText(texto)
      .then(() => {
        this.recoveryCodesCopied = true;
      })
      .catch(() => {
        this.toast.error('Não foi possível copiar. Anote os códigos da tela.');
      });
  }

  /** Devolve o switcher ao estado real da conta. */
  private syncSwitcher(): void {
    if (this.twoFactorSwitcher) {
      this.twoFactorSwitcher.checked = this.twoFactor?.enabled ?? false;
    }
  }

  /**
   * Aviso acima do switch, para quem ainda não ativou.
   *
   * Chamava-se `twoFactorDeadlineNotice` e anunciava a data de corte, que saiu
   * na CU-86akgt7pf. Sobraram dois casos — o perfil obrigado (Administrador e
   * Customer Success) e o resto, para quem a ativação é recomendação, não
   * exigência. O nome, o `data-testid` e a cor acompanharam: amarelo para o que
   * é exigido, azul para o que é conselho.
   */
  get twoFactorNotice(): string {
    if (!this.twoFactor || this.twoFactor.enabled) {
      return null;
    }

    return this.twoFactor.required
      ? 'A verificação em duas etapas é obrigatória para o seu perfil.'
      : 'Sua conta está protegida só pela senha. Ative a verificação em duas etapas para exigir também um código do aplicativo autenticador a cada login.';
  }

  /**
   * Amarelo só quando é exigência. Pintar de alerta, e para sempre, um item que
   * a diretoria tornou opcional contradiz a própria decisão — e um aviso que
   * grita todo dia sobre algo que ninguém precisa fazer é o que ensina o
   * usuário a ignorar avisos.
   */
  get twoFactorNoticeClass(): string {
    return this.twoFactor?.required ? 'alert-warning' : 'alert-info';
  }

  /**
   * O switch só liga.
   *
   * Desativar pelo painel deixou de existir: a proteção não pode ser removida
   * por quem está com a sessão na mão — que, num roubo de sessão, é exatamente
   * o atacante. Quem perdeu o autenticador abre chamado, e um administrador
   * gera uma chave nova pela tela de usuários.
   */
  toggleTwoFactor(): void {
    this.twoFactorError = null;
    this.twoFactorPassword = '';
    this.twoFactorCode = '';

    if (this.twoFactor?.enabled) {
      this.toast.error(
        'A verificação em duas etapas não pode ser desativada por aqui. '
        + 'Fale com o Suporte se perdeu o aplicativo autenticador.'
      );
      this.syncSwitcher();
      return;
    }

    this.twoFactorStep = 'setup';
    this.twoFactorSetup = null;
  }

  /** Passo 1 do cadastro: a senha libera o segredo e o QR. */
  requestTwoFactorSetup(): void {
    this.twoFactorError = null;
    this.twoFactorBusy = true;

    this.authService.startTwoFactorEnrollment(this.twoFactorPassword).subscribe(
      setup => {
        this.twoFactorBusy = false;
        this.twoFactorSetup = setup;
        this.twoFactorPassword = '';
      },
      err => {
        this.twoFactorBusy = false;
        this.twoFactorError = err.status === 422
          ? 'Senha inválida.'
          : 'Não foi possível iniciar a configuração.';
        this.syncSwitcher();
      }
    );
  }

  /** Passo 2: o primeiro código confirma o cadastro. */
  confirmTwoFactorSetup(): void {
    this.twoFactorError = null;
    this.twoFactorBusy = true;

    this.authService.confirmTwoFactorEnrollment(this.twoFactorCode).subscribe(
      response => {
        this.twoFactorBusy = false;
        this.twoFactorRecoveryCodes = response.recovery_codes;
        this.twoFactorStep = 'codes';
        this.twoFactorSetup = null;
        this.twoFactorCode = '';
        this.loadTwoFactorStatus();
        this.toast.success('Verificação em duas etapas ativada!');
      },
      err => {
        this.twoFactorBusy = false;
        this.twoFactorError = err.status === 422
          ? (err.error?.error ?? 'Código inválido. Confira o aplicativo autenticador.')
          : 'Não foi possível concluir a ativação.';
        this.syncSwitcher();
      }
    );
  }


  regenerateRecoveryCodes(): void {
    this.twoFactorError = null;
    this.twoFactorBusy = true;

    this.authService.regenerateRecoveryCodes(this.twoFactorPassword).subscribe(
      response => {
        this.twoFactorBusy = false;
        this.twoFactorPassword = '';
        this.twoFactorRecoveryCodes = response.recovery_codes;
        this.twoFactorStep = 'codes';
        this.loadTwoFactorStatus();
      },
      err => {
        this.twoFactorBusy = false;
        this.twoFactorError = err.status === 422
          ? 'Senha inválida.'
          : 'Não foi possível gerar novos códigos.';
      }
    );
  }

  closeTwoFactorPanel(): void {
    this.syncSwitcher();
    this.twoFactorStep = 'idle';
    this.twoFactorSetup = null;
    this.twoFactorRecoveryCodes = null;
    this.twoFactorPassword = '';
    this.twoFactorCode = '';
    this.twoFactorError = null;
    this.recoveryCodesCopied = false;
  }

  // --- Token dos postbacks --------------------------------------------------

  loadPostbackToken(): void {
    this.postbackTokenState = 'loading';

    this.subscriptions.push(
      this.postbackTokenService.get().subscribe(token => {
        this.postbackToken = token;
        this.postbackTokenState = token ? 'ready' : 'unavailable';
      })
    );
  }

  /**
   * Regenerar derruba de uma vez todas as URLs com o token atual: plataforma de
   * pagamento, formulário e automação param de entregar lead até receberem a URL
   * nova. Por isso passa por confirmação.
   */
  confirmRegeneratePostbackToken(): void {
    let message = 'Gerar um token novo muda <b>TODAS</b> as URLs de integração e de listas. ';
    message += 'As URLs com o token atual <b>param de funcionar na hora</b>: cada plataforma, ';
    message += 'formulário ou automação que usa uma delas precisa receber a URL nova. Deseja continuar?';

    this.confirmation.show(message, () => this.regeneratePostbackToken());
  }

  regeneratePostbackToken(): void {
    this.postbackTokenBusy = true;

    this.subscriptions.push(
      this.postbackTokenService.regenerate().subscribe({
        next: token => {
          this.postbackTokenBusy = false;
          this.postbackToken = token;
          this.postbackTokenState = 'ready';
          this.toast.success('Token novo gerado. Atualize as URLs nas suas integrações e formulários.');
        },
        error: () => {
          this.postbackTokenBusy = false;
          this.toast.error('Não foi possível gerar um token novo. Tente de novo.');
          // A resposta pode ter se perdido depois de o backend trocar o token:
          // recarregar evita seguir mostrando URL com um token que já não vale.
          this.postbackTokenService.invalidate();
          this.loadPostbackToken();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  update(): void {
    if (this.isMember) {
      return;
    }

    const user = this.userForm.getRawValue() as User;
    user.billing_update = true;

    if (this.diffNF == false) {
      user.billing_document = null;
      user.billing_company_name = null;
      user.billing_company_alias = null;
      user.billing_email = null;
      user.billing_im = null;
      user.billing_postcode = null;
      user.billing_street = null;
      user.billing_number = null;
      user.billing_complement = null;
      user.billing_district = null;
      user.billing_city = null;
      user.billing_state = null;
    }

    this.isSaving = true;
    this.subscriptions.push(
      this.userService.save(user).subscribe({
        next: user => {
          this.isSaving = false;
          this.user = user;
          this.toast.success('Dados alterados com sucesso!');

          if (this.authService.getSimulatedUser()) {
            this.authService.setSimulatedUser(user);
          } else {
            this.authService.setUser(user);
          }
        },
        error: () => { this.isSaving = false; },
      })
    );
  }

  formatCpfCnpj(e) {
    const cleanedValue = e.target.value.replace(/\D/g, '');

    if (e.target.value.length < this.oldCNPJCPF.length) {
      this.oldCNPJCPF = e.target.value
      return;
    }
    if (cleanedValue.length <= 11) {
      // Aplica máscara para CPF parcial
      const newValue = cleanedValue.replace(/(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})/, '$1.$2.$3-$4');
      this.oldCNPJCPF = newValue.toString();
      return e.target.value = newValue
    } else {
      // Aplica máscara para CNPJ parcial
      const newValue = cleanedValue.replace(/(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})/, '$1.$2.$3/$4-$5');
      this.oldCNPJCPF = newValue.toString();
      return e.target.value = newValue
    }
  }

  cepChanged(e): void {
    const postcode = e.target.value ?? '';

    if ([8, 9].includes(postcode.length)) {
      this.errorCEP = false;
      const cepNumbers = postcode.replace(/\D/, '');
      fetch(`https://viacep.com.br/ws/${cepNumbers}/json`)
        .then(res => res.json().then(data => {
          this.userForm.patchValue({
            billing_street: data.logradouro,
            billing_city: data.localidade,
            billing_district: data.bairro,
            billin_complement: data.complemento,
            billing_state: data.uf
          });

          const signal = e.target.value.includes('-') ? '' : '-';

          e.target.value = e.target.value.slice(0, 5) + signal + e.target.value.slice(5, e.target.value.length);

          setTimeout(() => {
            document.getElementById('billing_number')?.focus();
          }, 200);
        })).catch(e => {
          this.errorCEP = true;
          return
        })
    }
  }

}
